import type { Vector3 } from 'three';
import type { Highlight } from '../../highlight.class';
import type { HighlightManager } from '../highlight-manager.class';
import { calculateTransitionArc } from '../../arc-calculation';
import { easeInOutCubic } from '../../../animation/easing';
import { lerpVector3 } from '../../../animation/path-interpolation';

/**
 * Parameters for animating transition to a new target
 */
export interface AnimateTransitionToNewTargetParams {
	highlight: Highlight;
	previousTarget: Vector3;
	previousSource: Vector3;
	previousAimPoint?: Vector3;
}

/**
 * Animate the spotlight to a new target using an arc path.
 * This is used when switching highlights - the spotlight is reused.
 * Animates both the light position along the arc AND the spotlight direction.
 *
 * @param self The HighlightManager instance
 * @param params Transition parameters
 */
export const animateTransitionToNewTargetCfn = (
	self: HighlightManager,
	params: AnimateTransitionToNewTargetParams
): void => {
	const { highlight, previousTarget, previousSource, previousAimPoint } = params;

	// Access fields via public getters
	const circle = self.circle;
	const animationManager = self.animationManager;
	const animationDuration = self.animationDuration;
	const maxIntensity = self.maxIntensity;
	const debugMode = self.debugMode;

	if (!circle) return;

	// Calculate transition arc from previous position to new position
	const { points, midpoint } = calculateTransitionArc(
		previousTarget,
		previousSource,
		highlight.highlightTarget,
		highlight.highlightLightSource,
		circle
	);

	self.arcPoints = points;
	self.arcMidpoint = midpoint;
	self.animationPhase = 'arc-transition';

	// Store the spotlight AIM positions for direction interpolation
	// The spotlight should gradually rotate from looking at the old object's face to the new object's face
	// Use the actual face points (where spotlight aims), not the highlightTarget (where light is positioned)
	const startAimPos = previousAimPoint?.clone() ?? previousTarget.clone();
	const endAimPos = highlight.spotlightAimPoint; // The face point of the new object

	// Set up transition arc visualization if in debug mode
	if (debugMode) {
		highlight.setTransitionArc(points);
	}

	// Stop any existing animations
	animationManager.stop(`highlight-position-${highlight.objectId}`);
	animationManager.stop(`highlight-intensity-${highlight.objectId}`);

	// Animate along the arc path
	animationManager.animateAlongPath(
		`highlight-position-${highlight.objectId}`,
		points,
		animationDuration,
		(position) => {
			// Get animation progress
			const progress = animationManager.getProgress(`highlight-position-${highlight.objectId}`) ?? 0;
			const easedProgress = easeInOutCubic(progress);

			// Interpolate spotlight aim direction from old face point to new face point
			// This ensures smooth rotation of the spotlight direction
			const interpolatedAimPoint = lerpVector3(startAimPos, endAimPos, easedProgress);

			// Update light position AND aim direction
			highlight.updateLightPosition(position, interpolatedAimPoint);

			// V-shaped intensity curve: high at ends, low at middle
			if (easedProgress < 0.5) {
				// First half: fade out (max -> 0)
				const fadeOutProgress = easedProgress * 2;
				highlight.setIntensity(maxIntensity * (1 - fadeOutProgress));
			} else {
				// Second half: fade in (0 -> max)
				const fadeInProgress = (easedProgress - 0.5) * 2;
				highlight.setIntensity(maxIntensity * fadeInProgress);
			}
		},
		{
			onComplete: () => {
				// Final update - light at target position, pointing at new object's face
				// Pass the face point explicitly to ensure no direction jump
				highlight.updateLightPosition(highlight.highlightTarget, highlight.spotlightAimPoint);
				highlight.setIntensity(maxIntensity);
				highlight.hideTransitionArc();
				self.animationPhase = null;
				self.arcPoints = [];
				self.arcMidpoint = null;
				self.previousHighlightId = null;
			}
		}
	);
};
